/**
 * Site / company access switches for User Details modal (Related Companies tab).
 */

import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Building2, Power, FileCheck, Bell, Eye,
} from 'lucide-react';
import type { UserProfile, UserSitePermission } from '../../lib/api';

function Label({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={className}>{children}</div>;
}

export interface UserDetailsSiteAccessTabProps {
  userSitePermissions: UserSitePermission[];
  savingPermission: number | null;
  selectedUser: UserProfile;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
}

export function UserDetailsSiteAccessTab({
  userSitePermissions,
  savingPermission,
  selectedUser,
  isSelf,
  handleToggleSiteLink,
  handleToggleSitePermission,
}: UserDetailsSiteAccessTabProps) {
  if (userSitePermissions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Loading company access...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {userSitePermissions.map((perms) => {
        const isSaving = savingPermission === perms.site;
        return (
          <Card
            key={perms.site}
            className={`${perms.is_linked ? '' : 'opacity-60'} ${isSaving ? 'animate-pulse' : ''}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {perms.site_name}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {perms.is_linked ? 'Linked' : 'Not Linked'}
                  </span>
                  <Switch
                    checked={perms.is_linked}
                    onCheckedChange={(checked) => handleToggleSiteLink(perms.site, checked)}
                    disabled={isSelf(selectedUser) || isSaving}
                  />
                </div>
              </div>
              {perms.site_code ? (
                <p className="text-xs text-muted-foreground">{perms.site_code}</p>
              ) : null}
            </CardHeader>

            {perms.is_linked ? (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Power className="h-4 w-4" />
                    Active
                  </span>
                  <Switch
                    checked={perms.is_active}
                    onCheckedChange={(checked) =>
                      handleToggleSitePermission(perms.site, 'is_active', checked)
                    }
                    disabled={isSelf(selectedUser) || isSaving}
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Access Modules
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <FileCheck className="h-4 w-4" />
                        Sales Orders
                      </span>
                      <Switch
                        checked={perms.can_purchasing}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'can_purchasing', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    User Role
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Read Only
                      </span>
                      <Switch
                        checked={perms.is_read_only}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'is_read_only', checked)
                        }
                        disabled={isSelf(selectedUser) || isSaving}
                      />
                    </div>
                    {perms.is_read_only ? (
                      <p className="text-xs text-muted-foreground">
                        Read-only users cannot edit, and receive no notifications at all —
                        no bell alerts and no emails.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Notification Emails
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Procurement
                      </span>
                      <Switch
                        checked={perms.notify_procurement}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'notify_procurement', checked)
                        }
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
